import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Address, AddressError } from '../models';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { AddressifyAutocompleteVm } from '../models/addresify-autocomplete-vm';

@Injectable({
  providedIn: 'root',
})
export class AddressifyService {
  private addressifyApi =
    'https://api.addressify.com.au/addresspro/autocomplete';
  private addressifyKey = '01e24eaf-42ce-406a-ba81-70240ab83221';
  private defaultQuery: AddressifyAutocompleteQuery = {
    api_key: this.addressifyKey,
    term: '',
    address_types: 1,
    close_matches: true,
    highlight: false,
    info: true,
    max_results: 10,
  };

  constructor(private http: HttpClient) {}

  autocomplete(term: string): Observable<AddressError | Address[]> {
    return this.http
      .get<AddressifyAutocompleteVm>(this.addressifyApi, {
        params: { ...this.defaultQuery, term },
      })
      .pipe(
        map((vm) => {
          if (Array.isArray(vm)) {
            return vm.map((dto) => {
              const address1 =
                dto.BuildingName &&
                dto.AddressLine1?.startsWith(`${dto.BuildingName},`)
                  ? dto.AddressLine1.slice(dto.BuildingName.length + 1).trim()
                  : dto.AddressLine1;
              return {
                address1: address1 || dto.AddressLine2,
                address2: address1 ? dto.AddressLine2 : undefined,
                suburb: dto.Suburb,
                state: dto.State,
                postcode: dto.Postcode,
              } as Address;
            });
          } else {
            return {
              code: vm.errorCode,
              message: vm.errorMessage,
            } as AddressError;
          }
        })
      );
  }
}

interface HttpQueryString {
  [param: string]:
    | string
    | number
    | boolean
    | readonly (string | number | boolean)[];
}

interface AddressifyAutocompleteQuery extends HttpQueryString {
  address_types: number; //Sets the address types to return. 1 = All address types including physical and cater for PO Box style addresses. 2 = Valid physical addresses only. 3 = PO Box style addresses only. Set this to 2 if you only want to return physical addresses that exist and are valid. Leaving it to the default of 1 will allow the entry of PO Box style addresses which cannot be validated, but can usually be autocompleted and parsed. Default value: 1
  api_key: string; //Your API key.
  close_matches: boolean; //If set to true the list of addresses returned will include addresses deemed to be a close match to the entered address, loosely ordered with the most likely matches first Default value: false
  highlight: boolean; //If set to true smart highlighting is enabled and matching areas of the result are enclosed in <strong></strong> tags. Smart highlighting automatically accounts for abbreviations eg. COURT will highlight CT in the result set. If info=true has been passed the highlight tags will appear in the AddressFull field, if it has not been passed the tags will appear in the returned strings. You can easily replace the tags by doing a string replace to cater for custom styling. You can also replace the tags with an empty string for display purposes eg. you might set the items label to the highlighted string to display the highlighting in an autocomplete picklist, but then remove the tags from the items value so that the selected address appears without highlighting in your address field. Default value: false
  info: boolean; //If set to true an arrary of address info objects as returned by addresspro/info will be returned instead of an array of strings. Default value: false
  max_results: number; //The maximum number of results to return. Default value: 10 Size range: 1-20
  term: string; //The address string to complete.
}
